/**
 * Gumlet image CDN helpers for responsive, byte-efficient delivery.
 * @see https://docs.gumlet.com/ - typically `w` (width) and `format` for WebP.
 */

const WEBP = 'webp'

export function isGumletImageUrl(imageUrl: string): boolean {
  if (!imageUrl?.trim()) return false
  try {
    const url = new URL(imageUrl)
    return url.hostname.endsWith('gumlet.io')
  } catch {
    return false
  }
}

/**
 * Returns a Gumlet URL with width/quality hints for the full-size image.
 * Non-Gumlet URLs are returned unchanged.
 */
export function getGumletSizedImageUrl(
  imageUrl: string,
  width: number,
  options?: { format?: typeof WEBP }
): string {
  if (!isGumletImageUrl(imageUrl)) return imageUrl

  try {
    const url = new URL(imageUrl)
    url.searchParams.set('w', String(width))
    if (options?.format !== undefined) {
      url.searchParams.set('format', options.format)
    } else {
      url.searchParams.set('format', WEBP)
    }
    return url.toString()
  } catch {
    return imageUrl
  }
}

/**
 * Builds a `srcset` string for responsive `<img>` (width descriptors).
 */
export function buildGumletSrcSet(imageUrl: string, widths: number[]): string {
  if (!widths.length || !isGumletImageUrl(imageUrl)) return ''
  return widths
    .map(w => `${getGumletSizedImageUrl(imageUrl, w)} ${w}w`)
    .join(', ')
}

/** Homepage carousel: full-bleed hero up to 1280px max content width. */
export const CAROUSEL_IMAGE_SIZES = '(max-width: 1280px) 100vw, 1280px'

/** Widths for hero srcset (covers mobile through xl desktop). */
export const CAROUSEL_IMAGE_WIDTHS = [360, 640, 828, 1080, 1280] as const
